import React, { useEffect, useState } from 'react';
import { graphql } from '../gql';
import { useMutation, useQuery } from 'urql';
import { SeminarCard } from './SeminarCard.tsx';
import { usePublicCompany } from './hooks/usePublicCompany.ts';
import {
  ModulePrivileges,
  ParticipantStatus,
  PublicSeminarPageFieldType,
} from '../gql/graphql.ts';
import { PublicDetailsSidebar } from './PublicDetailsSidebar.tsx';
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from '@progress/kendo-react-layout';
import { cloneAndRemoveTypename } from './util.ts';
import { useUserInfo } from './hooks/useUserInfo.ts';
import { ConditionalWrapper } from '../shared/ConditionalWrapper.tsx';
import { PublicSeminarFilesTab } from './PublicSeminarFilesTab.tsx';
import { PublicSeminarSignUpsTab } from './PublicSeminarSignUpsTab.tsx';
import { PublicSeminarParticipantsTab } from './PublicSeminarParticipantsTab.tsx';
import { PublicSeminarCancelSignUpModal } from './PublicSeminarCancelSignUpModal.tsx';
import { BackButton } from '../shared/BackButton.tsx';
import {
  Link,
  Navigate,
  useCanGoBack,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { HeroIcon } from '../shared/HeroIcon.tsx';
import { canAccessAdminPanel } from './permissions.ts';

const CalendarEntriesForSeminarQuery = graphql(/* GraphQL */ `
  query getCalendarEntriesForSeminar($companyId: String!, $seminarId: String!) {
    calendarEventsForSeminar(companyId: $companyId, seminarId: $seminarId) {
      begin
      end
      title
    }
  }
`);

const PublicSeminarQuery = graphql(/* GraphQL */ `
  query getPublicSeminar(
    $seminarId: String
    $btsSeminarId: String
    $companyId: String!
  ) {
    publicSeminar(
      seminarId: $seminarId
      btsSeminarId: $btsSeminarId
      companyId: $companyId
    ) {
      id
      title
      subtitle
      seminarNumber
      startDate
      endDate
      dateText
      description
      locationText
      registrationDate
      price
      priceText
      price2
      priceText2
      price3
      priceText3
      price4
      priceText4
      isBookedOut
      isAlmostBookedOut
      canceled
      files {
        blobId
        accessPermissionLevel
        specificParticipantId
        pathToFile
        uploadedAt
        name
        size
      }
      body {
        type
        html
      }
      lecturers {
        name
        id
        jobTitle
        vita
        pathToProfileImage
      }
      categories {
        name
      }
      signUps {
        signUpId
        firstName
        lastName
        email
        canceled
      }
      participants {
        status
      }
      currentUserIsLecturer
      webinar
      webinarLink
      images
    }
  }
`);

const PublicParticipantsForSeminarQuery = graphql(/* GraphQL */ `
  query getPublicParticipantsSeminar($seminarId: String!, $companyId: String!) {
    participantsForSeminar(seminarId: $seminarId, companyId: $companyId) {
      firstName
      lastName
      email
      id
      hasUserAccount
      status
    }
  }
`);

const CancelSignUpMutation = graphql(/* GraphQL */ `
  mutation publicCancelSignUp($signUpId: Float!, $companyId: String!) {
    publicCancelSignUp(signUpId: $signUpId, companyId: $companyId)
  }
`);

interface PublicSeminarProps {
  seminarId?: string;
  btsSeminarId?: string;
  companyId: string;
}

export const PublicSeminar: React.FC<PublicSeminarProps> = ({
  seminarId = '',
  btsSeminarId,
  companyId,
}) => {
  const navigate = useNavigate();
  const router = useRouter();
  // Only fall back to browser back when there is an app-owned previous entry,
  // so users who landed on this page from outside the app end up at "/" rather
  // than being navigated off-site.
  const canGoBack = useCanGoBack();
  const goBack = () => {
    if (canGoBack) {
      router.history.back();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      navigate({ to: '/' as any });
    }
  };

  const [{ data, error, fetching }, refresh] = useQuery({
    query: PublicSeminarQuery,
    variables: {
      companyId,
      seminarId,
      btsSeminarId,
    },
    pause:
      companyId === undefined ||
      companyId === null ||
      ((seminarId === undefined || seminarId === null) &&
        (btsSeminarId === undefined || btsSeminarId === null)),
  });

  const [{ data: calendarEntriesData }] = useQuery({
    query: CalendarEntriesForSeminarQuery,
    variables: {
      companyId,
      seminarId,
    },
    pause:
      companyId === undefined ||
      companyId === null ||
      seminarId === undefined ||
      seminarId === null,
  });

  const { userInfo } = useUserInfo();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [seminarId, btsSeminarId]);

  useEffect(() => {
    if (!seminarId && btsSeminarId && data?.publicSeminar.id) {
      window.history.replaceState(
        null,
        '',
        `/seminar/${data?.publicSeminar.id}`,
      );
    }
  }, [seminarId, btsSeminarId, data?.publicSeminar.id]);

  const userHasParticipantListPrivilege = Boolean(
    userInfo?.data?.currentUser?.privileges.includes(
      ModulePrivileges.UseParticipantList,
    ),
  );

  const [{ data: dataParticipants }] = useQuery({
    query: PublicParticipantsForSeminarQuery,
    variables: {
      companyId,
      seminarId: data?.publicSeminar.id as string,
    },
    pause:
      !data?.publicSeminar?.id ||
      (!data?.publicSeminar.currentUserIsLecturer &&
        !userHasParticipantListPrivilege),
  });
  const activeSignUps =
    data?.publicSeminar.signUps.filter((s) => !s.canceled) ?? [];

  const [, cancelSignUp] = useMutation(CancelSignUpMutation);

  const { publicSeminar: seminar } = data || {};
  const [showCancelSignUpModalSignUpId, setShowCancelSignUpModalSignUpId] =
    useState<number | null>(null);

  const [publicCompanyData] = usePublicCompany();

  const getHtmlElement = (
    type: PublicSeminarPageFieldType,
    content: string,
  ) => {
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(content);
    const commonBoxClasses = `border px-4 py-3 rounded relative my-2 ${hasHtmlTags ? '' : 'whitespace-pre-wrap'}`;
    // Would love to replace this with an actual React component <Alert>, but this is raw html injected into the DOM, so can't use React components here
    const mapping: Record<string, string> = {
      error: `<div class="bg-red-100 border-red-400 text-red-700 prose-headings:text-red-700 ${commonBoxClasses}">${content}</div>`,
      info: `<div class="bg-sky-100 border-sky-400 text-sky-700 prose-headings:text-sky-700 ${commonBoxClasses}">${content}</div>`,
      warning: `<div class="bg-yellow-100 border-yellow-400 text-yellow-700 prose-headings:text-yellow-700 ${commonBoxClasses}">${content}</div>`,
      notice: `<div class="bg-slate-100 border-slate-400 text-slate-700 prose-headings:text-slate-700 ${commonBoxClasses}">${content}</div>`,
      heading: `<div class="font-bold text-3xl my-2">${content}</div>`,
      normal: `<div ${hasHtmlTags ? '' : `class="whitespace-pre-wrap"`}>${content}</div>`,
    };
    return mapping[type?.toLowerCase() || 'normal'];
  };

  const bodyHtml = seminar?.body
    ?.map(
      (item) => item.type && item.html && getHtmlElement(item.type, item.html),
    )
    .filter(Boolean)
    .join('');

  const isAdminForCompany = canAccessAdminPanel(
    userInfo?.data?.currentUser?.privileges || [],
  );

  const hasWaitlist =
    publicCompanyData?.functionalSettings?.waitlistActive &&
    seminar?.isBookedOut;

  const [selected, setSelected] = React.useState<number>(0);

  const isBeforeRegistrationDate = seminar?.registrationDate
    ? new Date(seminar?.registrationDate) > new Date()
    : true;

  const isBeforeEndOfSeminar = new Date(seminar?.endDate) > new Date();

  const userIsLecturerForThisSeminar = Boolean(
    data?.publicSeminar.currentUserIsLecturer,
  );
  const userIsSignedUp = Boolean(activeSignUps.length);
  const seminarHasFiles = Boolean(data?.publicSeminar.files.length);

  const userHasFilePrivileges = Boolean(
    userInfo?.data?.currentUser?.privileges?.includes(
      ModulePrivileges.CanAccessAllSpecificParticipantFiles,
    ) ||
    userInfo?.data?.currentUser?.privileges?.includes(
      ModulePrivileges.CanAccessAllParticipantsFiles,
    ) ||
    userInfo?.data?.currentUser?.privileges?.includes(
      ModulePrivileges.CanAccessAllLecturerFiles,
    ),
  );

  const userHasFileOrSeminarListPrivilege = Boolean(
    userHasFilePrivileges || userHasParticipantListPrivilege,
  );

  const userHasMultipleSignUps = (activeSignUps.length ?? 0) > 1;

  const userHasTabLayout =
    userHasMultipleSignUps ||
    userIsLecturerForThisSeminar ||
    (userIsSignedUp && seminarHasFiles) ||
    userHasFileOrSeminarListPrivilege;

  // Backend returns NotFoundException for non-visible (or missing) seminars.
  // Rather than render a layout shell with no data, send the user home.
  if (!fetching && (error || (data && !data.publicSeminar))) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <title>{`${seminar?.title || 'Seminar'} - ${import.meta.env.VITE_APP_TITLE}`}</title>
      <div className="col-span-1 flex flex-col gap-6 md:col-span-9">
        <div className="flex items-center justify-between">
          <BackButton onClick={goBack} />
          {isAdminForCompany && seminar?.id && (
            <ConditionalWrapper
              condition={!!window.__BTS_EMBED_MODE__}
              wrapper={(c) => (
                <a
                  className="flex cursor-pointer items-center gap-1.5 self-start border-b border-b-transparent text-zinc-800 hover:border-b-black hover:text-black"
                  href={`${window.__BTS_EMBED_BASE_PREFIX__ ?? ''}/admin/seminars/edit/${seminar?.id}?from=public`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {c}
                </a>
              )}
              falseWrapper={(c) => (
                <Link
                  className="flex cursor-pointer items-center gap-1.5 self-start border-b border-b-transparent text-zinc-800 hover:border-b-black hover:text-black"
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  to={`/admin/seminars/edit/${seminar?.id}` as any}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  search={{ from: 'public' } as any}
                >
                  {c}
                </Link>
              )}
            >
              <HeroIcon
                variant="outline"
                name={'Pencil'}
                className="inline-block h-4 w-4 shrink-0"
              />
              Editieren
            </ConditionalWrapper>
          )}
        </div>

        <ConditionalWrapper
          condition={userHasTabLayout}
          wrapper={(children) => {
            const tabStripTabClasses = '!p-0';

            return (
              <TabStrip
                selected={selected}
                onSelect={(e: TabStripSelectEventArguments) => {
                  setSelected(e.selected);
                }}
                animation={false}
                className="[&_.k-active_.k-link]:!text-gray-500 [&_.k-link]:!text-bts-theme-primary focus:[&_.k-tabstrip-item]:!shadow-none"
              >
                <TabStripTab
                  contentClassName={tabStripTabClasses}
                  title="Beschreibung"
                >
                  {children}
                </TabStripTab>
                {userHasMultipleSignUps && (
                  <TabStripTab
                    contentClassName={tabStripTabClasses + ' md:!p-0'}
                    title={`Anmeldungen (${activeSignUps.length})`}
                  >
                    <PublicSeminarSignUpsTab
                      signUps={activeSignUps}
                      onClickCancel={
                        publicCompanyData?.functionalSettings
                          ?.cancelSignUpActive
                          ? setShowCancelSignUpModalSignUpId
                          : undefined
                      }
                    />
                  </TabStripTab>
                )}
                {(userIsLecturerForThisSeminar ||
                  userHasParticipantListPrivilege) && (
                  <TabStripTab
                    contentClassName={tabStripTabClasses + ' md:!p-0'}
                    title={`Teilnehmer ${dataParticipants?.participantsForSeminar.length ? `(${dataParticipants?.participantsForSeminar.length})` : ''}`}
                  >
                    <PublicSeminarParticipantsTab
                      dataPublicSeminar={data}
                      dataParticipants={dataParticipants}
                      emailCurrentUser={
                        userInfo?.data?.currentUser?.address.email || undefined
                      }
                    />
                  </TabStripTab>
                )}
                {(userIsLecturerForThisSeminar ||
                  (userIsSignedUp && seminarHasFiles) ||
                  userHasFilePrivileges) && (
                  <TabStripTab
                    contentClassName={tabStripTabClasses + ' md:!p-0'}
                    title={`Dateien ${data?.publicSeminar?.files?.length ? `(${data?.publicSeminar.files?.length})` : ''}`}
                  >
                    <PublicSeminarFilesTab
                      seminarId={data?.publicSeminar.id as string}
                      companyId={companyId}
                      participants={
                        dataParticipants?.participantsForSeminar ?? []
                      }
                      files={
                        data?.publicSeminar.files &&
                        cloneAndRemoveTypename(data?.publicSeminar.files)
                      }
                      showUploadSection={
                        userIsLecturerForThisSeminar || userHasFilePrivileges
                      }
                      refreshData={refresh}
                      showSpecificParticipantFilesOption={userInfo?.data?.currentUser?.privileges?.includes(
                        ModulePrivileges.CanAccessAllSpecificParticipantFiles,
                      )}
                      showAllParticipantFilesOption={
                        userIsLecturerForThisSeminar ||
                        userInfo?.data?.currentUser?.privileges?.includes(
                          ModulePrivileges.CanAccessAllParticipantsFiles,
                        )
                      }
                      showAllLecturerFilesOption={
                        userIsLecturerForThisSeminar ||
                        userInfo?.data?.currentUser?.privileges?.includes(
                          ModulePrivileges.CanAccessAllLecturerFiles,
                        )
                      }
                    />
                  </TabStripTab>
                )}
              </TabStrip>
            );
          }}
        >
          <SeminarCard
            singleView
            images={
              publicCompanyData?.visualSettings?.showImages
                ? data?.publicSeminar.images
                : undefined
            }
            id={seminar?.seminarNumber}
            title={seminar?.title}
            description={bodyHtml}
            subtitle={seminar?.subtitle}
            category={
              seminar?.categories?.find((c) => Boolean(c.name.trim()))?.name
            }
            isSignedUp={Boolean(activeSignUps.length)}
            isParticipant={seminar?.participants.some(
              (p) => p.status === ParticipantStatus.Participant,
            )}
            isWaitlist={seminar?.participants.some(
              (p) => p.status === ParticipantStatus.Waitlist,
            )}
            isParticipationCanceled={
              seminar?.participants.some(
                (p) => p.status === ParticipantStatus.Canceled,
              ) || seminar?.signUps.some((s) => s.canceled)
            }
            signUpLink={
              publicCompanyData?.functionalSettings.registrationActive &&
              isBeforeEndOfSeminar &&
              isBeforeRegistrationDate &&
              !seminar?.canceled &&
              (!seminar?.isBookedOut || hasWaitlist)
                ? `/seminar/${seminar?.id}/sign-up`
                : undefined
            }
            signUpLinkCaption={
              hasWaitlist
                ? activeSignUps.length
                  ? 'Weitere Anmeldung auf der Warteliste'
                  : 'Auf die Warteliste'
                : activeSignUps.length
                  ? 'Weitere Person anmelden'
                  : 'Anmelden'
            }
            signUpLinkLogo={hasWaitlist ? 'QueueList' : 'AcademicCap'}
            onCancelSignUpClick={
              activeSignUps.length === 1 &&
              publicCompanyData?.functionalSettings.cancelSignUpActive
                ? () => {
                    setShowCancelSignUpModalSignUpId(activeSignUps[0].signUpId);
                  }
                : undefined
            }
            tabbed={userHasTabLayout}
          />
        </ConditionalWrapper>
      </div>
      {seminar && (
        <PublicDetailsSidebar
          seminar={seminar}
          calendarEvents={
            publicCompanyData?.visualSettings.showCalendarEvents
              ? calendarEntriesData?.calendarEventsForSeminar
              : []
          }
          showSignalLight={
            publicCompanyData?.visualSettings.showSignalLight &&
            !seminar.canceled
          }
          showWebinarLink={
            userIsLecturerForThisSeminar ||
            isAdminForCompany ||
            seminar.participants.some(
              (p) => p.status === ParticipantStatus.Participant,
            )
          }
        />
      )}
      {showCancelSignUpModalSignUpId && (
        <PublicSeminarCancelSignUpModal
          onClose={() => {
            setShowCancelSignUpModalSignUpId(null);
          }}
          startDate={new Date(seminar?.startDate)}
          onClickCancel={() => {
            cancelSignUp({
              companyId: companyId,
              signUpId: showCancelSignUpModalSignUpId,
            }).then(() => {
              refresh();
              setShowCancelSignUpModalSignUpId(null);
            });
          }}
        />
      )}
    </>
  );
};
